import { useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import {
  useGetSuggestedActionsQuery,
  useGetNoteActionsQuery,
  useAcceptActionMutation,
  useRejectActionMutation,
  useCreateTestActionMutation,
} from './api';
import {
  selectExpandedCardId,
  makeSelectIsCardExpanded,
  selectDraftForAction,
  selectHistoryForAction,
  selectCanUndo,
  selectCanRedo,
  selectHasUnsavedDrafts,
} from './selectors';
import {
  expandCard,
  collapseCard,
  toggleCardExpansion,
  updateDraft,
  clearDraft,
  clearAllDraftsForAction,
  undo,
  redo,
  loadDraftForAction,
  resetDraft,
  initializeDraft,
} from './slice';
import { useAppSelector, useAppDispatch, usePagination } from '@store/hooks';
import { ActionScope, Action, ActionPayloadMap } from '../types';

/**
 * Hook to fetch suggested actions with pagination
 * Uses the generic pagination utility from store/hooks.ts
 *
 * @param scope - The scope of actions to fetch (dashboard or note)
 * @param noteId - The ID of the note to fetch actions for (required for note scope)
 * @param perPage - Number of items per page
 * @param pollingInterval - Optional interval in ms for polling (0 means no polling)
 * @param refetchOnFocus - Whether to refetch when the window gains focus
 */
export function usePaginatedActions(
  scope: ActionScope,
  noteId?: string,
  perPage = 20,
  pollingInterval = 0,
  refetchOnFocus = false,
) {
  // Create argument generator based on scope
  const getQueryArgs = useCallback(
    (page: number, limit: number) => {
      if (scope === 'dashboard') {
        return { page, perPage: limit };
      } else {
        return { noteId: noteId || '', page, perPage: limit };
      }
    },
    [scope, noteId],
  );

  // Use the generic pagination hook with scope-based query
  const paginationData = usePagination<Action, any>(
    args => {
      if (scope === 'dashboard') {
        return useGetSuggestedActionsQuery(args, {
          skip: scope !== 'dashboard',
          // Add polling for dashboard scope
          pollingInterval: pollingInterval,
          refetchOnFocus: refetchOnFocus,
        });
      } else {
        return useGetNoteActionsQuery(args, {
          skip: scope !== 'note' || !noteId,
          // Add polling for note scope
          pollingInterval: pollingInterval,
          refetchOnFocus: refetchOnFocus,
        });
      }
    },
    getQueryArgs,
    1,
    perPage,
  );

  return {
    data: paginationData.items,
    isLoading: paginationData.isLoading,
    isError: paginationData.isError,
    isFetching: paginationData.isFetching,
    hasNextPage: paginationData.hasNextPage,
    fetchNextPage: paginationData.fetchNextPage,
    isFetchingNextPage: paginationData.isFetching && paginationData.page > 1,
    refetch: paginationData.reset, // Expose reset as refetch to allow manual refresh
  };
}

/**
 * Hook for card expansion functionality
 */
export function useCardExpansion() {
  const dispatch = useAppDispatch();
  const expandedCardId = useAppSelector(selectExpandedCardId);

  return {
    expandedCardId,
    isExpanded: (id: string) => {
      // Create a memoized selector for this ID
      const selectIsCardExpanded = makeSelectIsCardExpanded(id);
      return useAppSelector(selectIsCardExpanded);
    },
    expandCard: (id: string) => {
      dispatch(expandCard(id));
    },
    collapseCard: (id?: string) => {
      dispatch(collapseCard(id));
    },
    toggleExpansion: (id: string) => {
      dispatch(toggleCardExpansion(id));
    },
  };
}

/**
 * Hook to accept an action
 */
export function useAcceptAction() {
  const [acceptAction, result] = useAcceptActionMutation();

  const mutate = useCallback(
    (
      { actionId, payload }: { actionId: string; payload: { action_payload?: any } },
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => {
      const promise = acceptAction({ actionId, actionAccept: payload });

      // Handle options manually since the hook doesn't directly expose them
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [acceptAction],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook to reject an action
 */
export function useRejectAction() {
  const [rejectAction, result] = useRejectActionMutation();

  const mutate = useCallback(
    (
      actionId: string,
      rejectionData?: {
        rejection_reason?: string;
        rejection_payload?: Record<string, any>;
      },
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => {
      const promise = rejectAction({
        actionId,
        actionReject: rejectionData || {},
      });

      // Handle options manually since the hook doesn't directly expose them
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [rejectAction],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook to create a test action
 */
export function useCreateTestAction() {
  const [createTestAction, result] = useCreateTestActionMutation();

  const mutate = useCallback(
    ({
      actionType,
      encounterId,
      payload,
    }: {
      actionType: keyof ActionPayloadMap;
      encounterId: string;
      payload: ActionPayloadMap[typeof actionType];
    }) => {
      return createTestAction({ actionType, encounterId, payload });
    },
    [createTestAction],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook to get the count of suggested actions
 */
export function useSuggestedActionsCount(args?: {
  page?: number;
  perPage?: number;
  noteId?: string;
}) {
  const dashboardQuery = useGetSuggestedActionsQuery(
    {
      page: args?.page ?? 1,
      perPage: args?.perPage ?? 100,
    } as any,
    {
      skip: !!args?.noteId,
      selectFromResult: ({ data }) => ({
        data: data?.total ?? 0,
      }),
    },
  );

  const noteQuery = useGetNoteActionsQuery(
    {
      noteId: args?.noteId || '',
      page: args?.page ?? 1,
      perPage: args?.perPage ?? 100,
    } as any,
    {
      skip: !args?.noteId,
      selectFromResult: ({ data }) => ({
        data: data?.total ?? 0,
      }),
    },
  );

  return args?.noteId ? noteQuery.data : dashboardQuery.data;
}

// Draft management hooks

/**
 * Draft management hook for any action type
 */
export function useActionDraft<T extends Record<string, any>>(actionId: string) {
  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectDraftForAction(actionId));
  const history = useAppSelector(selectHistoryForAction(actionId));
  const canUndo = useAppSelector(selectCanUndo(actionId));
  const canRedo = useAppSelector(selectCanRedo(actionId));

  // Load draft on mount
  useEffect(() => {
    if (actionId) {
      dispatch(loadDraftForAction({ actionId }));
    }
  }, [actionId, dispatch]);

  const updateDraftContent = useCallback(
    (updates: Partial<T>) => {
      dispatch(updateDraft({ actionId, data: updates }));
    },
    [actionId, dispatch],
  );

  const clearDraftContent = useCallback(() => {
    dispatch(clearDraft(actionId));
  }, [actionId, dispatch]);

  const clearAllForAction = useCallback(() => {
    dispatch(clearAllDraftsForAction(actionId));
  }, [actionId, dispatch]);

  const performUndo = useCallback(() => {
    dispatch(undo(actionId));
  }, [actionId, dispatch]);

  const performRedo = useCallback(() => {
    dispatch(redo(actionId));
  }, [actionId, dispatch]);

  const reset = useCallback(
    (originalData: T) => {
      dispatch(resetDraft({ actionId, originalData }));
    },
    [actionId, dispatch],
  );

  return {
    draft: draft as { actionId: string; data: T; lastSaved: number; isDirty: boolean } | undefined,
    history,
    canUndo,
    canRedo,
    updateDraft: updateDraftContent,
    clearDraft: clearDraftContent,
    clearAll: clearAllForAction,
    undo: performUndo,
    redo: performRedo,
    reset,
  };
}

/**
 * Hook for editor integration with any action type
 */
export function useActionEditor<T extends Record<string, any>>(
  actionId: string,
  debounceMs: number = 1000,
) {
  const dispatch = useAppDispatch();
  const { draft, updateDraft, clearDraft, undo, redo, canUndo, canRedo, reset } =
    useActionDraft<T>(actionId);

  // Create debounced update function
  const debouncedUpdateDraft = useMemo(
    () => debounce(updateDraft, debounceMs),
    [updateDraft, debounceMs],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateDraft.cancel();
    };
  }, [debouncedUpdateDraft]);

  // Initialize draft without marking as dirty
  const initializeDraftContent = useCallback(
    (content: T) => {
      dispatch(initializeDraft({ actionId, data: content }));
    },
    [actionId, dispatch],
  );

  const handleContentChange = useCallback(
    (content: Partial<T>) => {
      // Use debounced update to avoid firing on every keystroke
      debouncedUpdateDraft(content);
    },
    [debouncedUpdateDraft],
  );

  const handleContentChangeImmediate = useCallback(
    (content: Partial<T>) => {
      // Use this for immediate updates when needed
      updateDraft(content);
    },
    [updateDraft],
  );

  const handleSave = useCallback(async () => {
    // Cancel any pending debounced updates
    debouncedUpdateDraft.cancel();
    // Return true to indicate save intent
    // Actual save logic will be handled by the modal
    return true;
  }, [debouncedUpdateDraft]);

  const handleClose = useCallback(() => {
    // Cancel any pending debounced updates
    debouncedUpdateDraft.cancel();
    // Optionally clear draft on close
    // This depends on UX requirements
  }, [debouncedUpdateDraft]);

  // Wrap undo/redo to cancel pending updates
  const handleUndo = useCallback(() => {
    debouncedUpdateDraft.cancel();
    undo();
  }, [debouncedUpdateDraft, undo]);

  const handleRedo = useCallback(() => {
    debouncedUpdateDraft.cancel();
    redo();
  }, [debouncedUpdateDraft, redo]);

  return {
    draft,
    canUndo,
    canRedo,
    initializeDraft: initializeDraftContent,
    handleContentChange,
    handleContentChangeImmediate,
    handleSave,
    handleClose,
    undo: handleUndo,
    redo: handleRedo,
    clearDraft,
    reset,
  };
}

/**
 * Hook for checking unsaved changes across all actions
 */
export function useHasUnsavedChanges() {
  const hasUnsaved = useAppSelector(selectHasUnsavedDrafts);
  return hasUnsaved;
}
